import { useState, useEffect, useCallback } from 'react';

/**
 * useChargeForm - Hook pour gérer le formulaire de charge
 *
 * @param {boolean} show - État d'affichage de la modale
 * @param {string} mode - Mode du formulaire ('create', 'edit', 'edit-group')
 * @param {Object} charge - Charge à éditer (null en création)
 * @param {number} filterAnnee - Année filtrée
 * @param {Array} existingCharges - Charges existantes pour vérification doublons
 * @param {Function} onSubmit - Callback de soumission
 * @param {Function} onClose - Callback de fermeture
 */
export function useChargeForm(show, mode, charge, filterAnnee, existingCharges, onSubmit, onClose) {
  const getInitialFormData = useCallback(() => ({
    nom: '',
    type: 'Mensuelle',
    annee: filterAnnee || new Date().getFullYear(),
    mois: '',
    trimestre: '',
    montant: 0,
    consultant: '',
    tousLesMois: false,
    tousLesTrimestres: false
  }), [filterAnnee]);

  const [formData, setFormData] = useState(getInitialFormData);
  const [submitting, setSubmitting] = useState(false);
  const [nomError, setNomError] = useState('');

  // Initialiser le formulaire selon le mode
  useEffect(() => {
    if (mode === 'edit' && charge) {
      setFormData({
        nom: charge.nom,
        type: charge.type,
        annee: charge.annee,
        mois: charge.mois || '',
        trimestre: charge.trimestre || '',
        montant: charge.montant,
        consultant: charge.consultant || '',
        tousLesMois: false,
        tousLesTrimestres: false
      });
    } else if (mode === 'edit-group' && charge) {
      setFormData({
        nom: charge.nom,
        type: charge.type,
        annee: filterAnnee,
        mois: '',
        trimestre: '',
        montant: charge.charges[0].montant,
        consultant: charge.charges[0].consultant || '',
        tousLesMois: false,
        tousLesTrimestres: false
      });
    } else {
      setFormData(getInitialFormData());
    }
    setNomError('');
  }, [mode, charge, filterAnnee, show, getInitialFormData]);

  // Vérifier si un nom existe déjà
  const checkDuplicateName = useCallback((nom) => {
    if (!nom.trim()) return false;
    const normalizedNom = nom.trim().toLowerCase();

    // En mode edit, exclure la charge en cours d'édition
    const currentChargeId = charge?.id;
    const currentChargeIds = mode === 'edit-group' && charge?.charges
      ? charge.charges.map(c => c.id)
      : [];

    return existingCharges.some(c => {
      if (currentChargeId && c.id === currentChargeId) return false;
      if (currentChargeIds.length > 0 && currentChargeIds.includes(c.id)) return false;
      return c.nom.trim().toLowerCase() === normalizedNom;
    });
  }, [charge, mode, existingCharges]);

  // Handler de changement
  const handleInputChange = useCallback((e) => {
    const { name, value, type: inputType, checked } = e.target;

    if (inputType === 'checkbox') {
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: name === 'montant' || name === 'annee' || name === 'mois'
          ? (value === '' ? '' : Number(value))
          : value
      }));

      // Vérifier les doublons pour le champ nom
      if (name === 'nom') {
        if (checkDuplicateName(value)) {
          setNomError('Une charge avec ce nom existe déjà');
        } else {
          setNomError('');
        }
      }
    }
  }, [checkDuplicateName]);

  // Handler de soumission
  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();

    // Vérifier les doublons avant soumission
    if (checkDuplicateName(formData.nom)) {
      setNomError('Une charge avec ce nom existe déjà');
      return;
    }

    setSubmitting(true);
    try {
      await onSubmit(formData);
      onClose();
    } catch (error) {
      // Error handled by parent
    } finally {
      setSubmitting(false);
    }
  }, [formData, checkDuplicateName, onSubmit, onClose]);

  return {
    formData,
    submitting,
    nomError,
    handleInputChange,
    handleSubmit
  };
}
